/**
 * Collections Dashboard - Main Controller
 * Delegates feature areas to focused modules for maintainability.
 */

import { CollectionAPI } from '../collections/CollectionAPI.js';
import { initDashboardEnhancements } from './DashboardEnhancements.js';
import { DASHBOARD_CONFIG } from './config.js';

import * as collectionLoader from './modules/collection-loader.js';
import * as filterControls from './modules/filter-controls.js';
import * as uiStates from './modules/ui-states.js';
import * as pagination from './modules/pagination.js';
import * as createCollectionModal from './modules/create-collection-modal.js';
import * as editCollectionModal from './modules/edit-collection-modal.js';
import * as collectionActions from './modules/collection-actions.js';
import * as itemsModal from './modules/items-modal.js';
import * as bulkActions from './modules/bulk-actions.js';
import * as tagsModule from './modules/tags.js';
import { initFilterDropdowns } from './modules/filter-dropdowns.js';
import * as bulkActionsUI from './modules/bulk-actions-ui.js';
import * as collectionRenderer from './modules/collection-renderer.js';

class CollectionsDashboard {
    /**
     * Instantiate the collections dashboard controller.
     */
    constructor() {
        this.api = new CollectionAPI();
        this.currentPage = 1;
        this.perPage = DASHBOARD_CONFIG.DEFAULT_PER_PAGE;
        this.totalPages = 1;
        this.totalCollections = 0;
        this.currentFilters = {
            search: '',
            tags: '',
            visibility: '',
            sort: 'created_desc'
        };
        this.isLoading = false;
        this.collections = [];

        this.initializeElements();
        this.attachEventListeners();
        this.initBulkActionsUI();
        this.initCollectionRenderer();
        this.attachCollectionEventDelegation();
        initFilterDropdowns(this);
        this.loadCollections();
        this.loadTags();
        
        /* Initialize edit modal */
        this.initEditModal();

        this.enhancements = initDashboardEnhancements(this);
    }

    initializeElements() {
        this.loadingState = document.getElementById('collections-loading');
        this.emptyState = document.getElementById('collections-empty');
        this.errorState = document.getElementById('collections-error');
        this.listContainer = document.getElementById('collections-list-container');
        this.pagination = document.getElementById('collections-pagination');

        this.createModal = document.getElementById('create-collection-modal');
        this.createForm = document.getElementById('create-collection-form');

        this.searchInput = document.getElementById('collections-search');
        this.tagsFilterBtn = document.getElementById('tags-filter-btn');
        this.visibilityFilterBtn = document.getElementById('visibility-filter-btn');
        this.sortFilterBtn = document.getElementById('sort-filter-btn');

        this.paginationInfo = document.getElementById('pagination-info');
        this.paginationPrev = document.getElementById('pagination-prev');
        this.paginationNext = document.getElementById('pagination-next');
        this.paginationCurrent = document.getElementById('pagination-current');
        this.paginationTotal = document.getElementById('pagination-total');
    }

    attachEventListeners() {
        document.getElementById('create-collection-btn')?.addEventListener('click', () => this.showCreateModal());
        document.getElementById('create-first-collection-btn')?.addEventListener('click', () => this.showCreateModal());
        document.getElementById('close-create-modal')?.addEventListener('click', () => this.hideCreateModal());
        document.getElementById('cancel-create-collection')?.addEventListener('click', () => this.hideCreateModal());

        this.createForm?.addEventListener('submit', (event) => this.handleCreateSubmit(event));

        if (this.searchInput) {
            let searchTimeout;
            this.searchInput.addEventListener('input', (event) => {
                clearTimeout(searchTimeout);
                searchTimeout = setTimeout(() => {
                    this.currentFilters.search = event.target.value.trim();
                    this.resetToFirstPage();
                    this.loadCollections();
                }, DASHBOARD_CONFIG.SEARCH_DEBOUNCE_MS);
            });
        }

        this.attachFilterListeners();

        this.paginationPrev?.addEventListener('click', () => this.previousPage());
        this.paginationNext?.addEventListener('click', () => this.nextPage());

        document.getElementById('collections-retry-btn')?.addEventListener('click', () => this.loadCollections());

        this.createModal?.addEventListener('click', (event) => {
            if (event.target === this.createModal) {
                this.hideCreateModal();
            }
        });

        document.addEventListener('keydown', (event) => {
            if (event.key === 'Escape' && !this.createModal?.classList.contains('hidden')) {
                this.hideCreateModal();
            }
        });

    }

    /**
     * Attach delegated event listeners for per-collection actions.
     *
     * @returns {void}
     */
    attachCollectionEventDelegation() {
        if (!this.listContainer) {
            return;
        }

        this.listContainer.addEventListener('click', (event) => {
            const actionEl = event.target.closest('[data-collection-action]');
            if (!actionEl) {
                return;
            }

            const actionType = actionEl.dataset.collectionAction;
            const collectionId = actionEl.dataset.collectionId || actionEl.closest('[data-collection-id]')?.dataset.collectionId;

            if (!actionType || !collectionId) {
                return;
            }

            event.preventDefault();
            this.handleCollectionAction(actionType, Number(collectionId), actionEl);
        });
    }

    /**
     * Dispatch collection actions that bubble up from delegated handlers.
     *
     * @param {string} action - Action identifier from data attributes.
     * @param {number} collectionId - Target collection identifier.
     * @param {HTMLElement} [sourceEl] - Element that triggered the action.
     * @returns {void}
     */
    handleCollectionAction(action, collectionId, sourceEl) {
        if (!action || Number.isNaN(collectionId)) {
            return;
        }

        const handlers = {
            'view-items': () => this.viewCollectionItems(collectionId),
            'edit': () => this.editCollection(collectionId),
            'duplicate': () => this.duplicateCollection(collectionId),
            'delete': () => this.deleteCollection(collectionId),
            'share': () => this.shareCollection(collectionId),
            'view-public': () => this.viewPublicCollection(collectionId),
            'toggle-visibility': () => {
                const nextVisibility = sourceEl?.dataset.nextVisibility;
                const shouldBePublic = nextVisibility === 'public';
                this.toggleVisibility(collectionId, shouldBePublic);
            },
        };

        const handler = handlers[action];
        if (handler) {
            handler();
        }

        const dropdownMenu = sourceEl?.closest('.actions-dropdown-menu');
        if (dropdownMenu) {
            this.closeAllActionDropdowns();
        }
    }
}

Object.assign(CollectionsDashboard.prototype, {
    ...collectionLoader,
    ...filterControls,
    ...uiStates,
    ...pagination,
    ...createCollectionModal,
    ...editCollectionModal,
    ...collectionActions,
    ...itemsModal,
    ...bulkActions,
    ...tagsModule,
    ...bulkActionsUI,
    ...collectionRenderer
});

export function initCollectionsDashboard() {
    if (document.querySelector('.collections-dashboard')) {
        new CollectionsDashboard();
    }
}

if (typeof window !== 'undefined' && document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCollectionsDashboard);
} else if (typeof window !== 'undefined') {
    initCollectionsDashboard();
}

window.initCollectionsDashboard = initCollectionsDashboard;
